import React, { useState, useEffect } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import {
  Settings,
  Clock,
  Target,
  TrendingUp,
  Zap,
  Battery,
  Calendar,
  CheckCircle,
  AlertCircle,
  Info,
  Save,
  RefreshCw,
  AlertTriangle
} from 'lucide-react';

/**
 * Student Control Panel
 * Allows students to configure their timeline preferences, pace settings,
 * and workload capacity to customize their college application experience
 */
const StudentControlPanel = () => {
  const { currentUser } = useFamilyContext();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    disclosure_pace: 'balanced', // conservative, balanced, aggressive
    workload_capacity: 'moderate', // light, moderate, intensive
    available_hours_per_week: 8,
    early_decision_priority: true,
    reveal_advanced_tasks: false,
    academic_calendar_awareness: true,
    reminder_frequency: 'weekly' // daily, weekly, bi_weekly
  });

  useEffect(() => {
    loadStudentPreferences();
  }, [currentUser?.id]);

  const loadStudentPreferences = async () => {
    if (!currentUser?.id) return;

    try {
      setLoading(true);
      
      // Check if preferences exist
      const { data: existing, error: fetchError } = await supabase
        .from('student_timeline_preferences')
        .select('*')
        .eq('user_id', currentUser.id)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError;
      }

      if (existing) {
        setPreferences({
          disclosure_pace: existing.disclosure_pace || 'balanced',
          workload_capacity: existing.workload_capacity || 'moderate',
          available_hours_per_week: existing.available_hours_per_week || 8,
          early_decision_priority: existing.early_decision_priority || false,
          reveal_advanced_tasks: existing.reveal_advanced_tasks || false,
          academic_calendar_awareness: existing.academic_calendar_awareness !== false,
          reminder_frequency: existing.reminder_frequency || 'weekly'
        });
      }
    } catch (error) {
      console.error('Error loading student preferences:', error);
      toast.error('Failed to load your preferences');
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    if (!currentUser?.id) {
      console.error('[StudentControlPanel] No current user ID available for save operation');
      toast.error('User not found - please log in again');
      return;
    }

    try {
      setSaving(true);
      console.log('[StudentControlPanel] Starting save operation for user:', currentUser.id);
      console.log('[StudentControlPanel] Preferences to save:', preferences);

      // First, verify we can connect to the database
      const { data: testConnection } = await supabase.from('student_timeline_preferences').select('user_id').limit(1);
      console.log('[StudentControlPanel] Database connection test:', testConnection ? 'SUCCESS' : 'FAILED');

      // Prepare upsert data
      const upsertData = {
        user_id: currentUser.id,
        ...preferences,
        updated_at: new Date().toISOString()
      };
      
      console.log('[StudentControlPanel] Upsert data prepared:', upsertData);

      // Attempt upsert with detailed logging
      const { data, error } = await supabase
        .from('student_timeline_preferences')
        .upsert(upsertData, {
          onConflict: 'user_id'
        })
        .select(); // Add select to see what was actually saved

      console.log('[StudentControlPanel] Upsert response:', { data, error });

      if (error) {
        console.error('[StudentControlPanel] Database error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        throw error;
      }

      console.log('[StudentControlPanel] Save successful:', data);

      // Verify the save by reading it back
      const { data: verification, error: verifyError } = await supabase
        .from('student_timeline_preferences')
        .select('*')
        .eq('user_id', currentUser.id)
        .single();

      if (verifyError) {
        console.error('[StudentControlPanel] Verification read failed:', verifyError);
        toast.error('Save may have failed - please refresh and check your settings');
        return;
      }

      console.log('[StudentControlPanel] Verification successful - data in database:', verification);
      toast.success('Timeline preferences saved successfully!');
      
    } catch (error) {
      console.error('[StudentControlPanel] Save operation failed:', error);
      console.error('[StudentControlPanel] Error stack:', error.stack);
      
      // Provide more specific error messages
      if (error.message?.includes('permission')) {
        toast.error('Permission denied - please contact support');
      } else if (error.message?.includes('network')) {
        toast.error('Network error - please check your connection and try again');
      } else {
        toast.error(`Failed to save preferences: ${error.message}`);
      }
    } finally {
      setSaving(false);
      console.log('[StudentControlPanel] Save operation completed');
    }
  };

  const handleRevealAdvancedTasks = () => {
    setPreferences(prev => ({
      ...prev,
      reveal_advanced_tasks: true
    }));
    
    toast((t) => (
      <div className="flex items-start space-x-3">
        <TrendingUp className="w-5 h-5 text-azure-500 mt-0.5" />
        <div>
          <p className="font-semibold text-slate-900">Advanced Tasks Revealed!</p>
          <p className="text-sm text-slate-600 mt-1">
            You now have access to additional research, networking, and optimization tasks.
          </p>
          <button
            onClick={() => {
              savePreferences();
              toast.dismiss(t.id);
            }}
            className="mt-2 px-3 py-1 bg-azure-100 text-azure-800 rounded text-xs font-medium hover:bg-azure-200"
          >
            Save Changes
          </button>
        </div>
      </div>
    ), {
      duration: 8000,
      style: { maxWidth: '400px' }
    });
  };

  const getPaceDescription = (pace) => {
    switch (pace) {
      case 'conservative':
        return 'Steady and thorough approach with extra time for review';
      case 'balanced':
        return 'Standard timeline with balanced workload distribution';
      case 'aggressive':
        return 'Fast-paced with compressed deadlines for early completion';
      default:
        return '';
    }
  };

  // Calculate timeline capacity and warnings
  const calculateTimelineCapacity = () => {
    const { disclosure_pace, workload_capacity, available_hours_per_week } = preferences;
    
    // Estimated task counts and hours by category
    const taskEstimates = {
      conservative: { taskCount: 15, totalHours: 45, essential: true },
      balanced: { taskCount: 25, totalHours: 75, essential: false },
      aggressive: { taskCount: 40, totalHours: 120, essential: false }
    };
    
    // Get current pace requirements
    const currentPace = taskEstimates[disclosure_pace] || taskEstimates.balanced;
    
    // Calculate weeks available (assuming 16-week timeline for comprehensive)
    const weeksAvailable = disclosure_pace === 'conservative' ? 8 : 
                          disclosure_pace === 'balanced' ? 12 : 16;
    
    const totalHoursNeeded = currentPace.totalHours;
    const totalHoursAvailable = available_hours_per_week * weeksAvailable;
    
    const isCapacityAdequate = totalHoursAvailable >= totalHoursNeeded;
    const utilizationRate = (totalHoursNeeded / totalHoursAvailable) * 100;
    
    // Tasks that would be removed/compromised
    const compromisedTasks = [];
    
    if (!isCapacityAdequate) {
      if (disclosure_pace === 'balanced' || disclosure_pace === 'aggressive') {
        compromisedTasks.push('Advanced essay research and multiple drafts');
        compromisedTasks.push('Optional interview preparation');
        compromisedTasks.push('Extra safety school research');
      }
      if (disclosure_pace === 'aggressive') {
        compromisedTasks.push('Scholarship search and applications');
        compromisedTasks.push('Summer program applications');
        compromisedTasks.push('Portfolio/creative supplement optimization');
      }
    }
    
    const recommendedHours = Math.ceil(totalHoursNeeded / weeksAvailable);
    
    return {
      isCapacityAdequate,
      utilizationRate: Math.round(utilizationRate),
      totalHoursNeeded,
      totalHoursAvailable,
      weeksAvailable,
      compromisedTasks,
      recommendedHours,
      currentPace: disclosure_pace
    };
  };

  const getCapacityDescription = (capacity) => {
    switch (capacity) {
      case 'light':
        return '3-5 hours per week - minimal additional commitment';
      case 'moderate':
        return '6-10 hours per week - balanced with other activities';
      case 'intensive':
        return '10+ hours per week - college applications as top priority';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <RefreshCw className="w-5 h-5 animate-spin text-azure-600" />
          <span className="text-slate-600">Loading your preferences...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <Settings className="w-8 h-8 text-azure-600" />
            <h1 className="text-3xl font-bold text-slate-900">Timeline Control Panel</h1>
          </div>
          <p className="text-slate-600">
            Customize your college application timeline to match your schedule, pace, and goals.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Settings */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Disclosure Pace */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center space-x-3 mb-4">
                <Clock className="w-6 h-6 text-azure-600" />
                <h2 className="text-xl font-semibold text-slate-900">Disclosure Pace</h2>
              </div>
              <p className="text-slate-600 mb-4">
                How quickly would you like new tasks and deadlines to be revealed?
              </p>
              
              <div className="space-y-3">
                {[
                  { value: 'conservative', label: 'Conservative', icon: Battery, color: 'emerald' },
                  { value: 'balanced', label: 'Balanced', icon: Target, color: 'azure' },
                  { value: 'aggressive', label: 'Aggressive', icon: Zap, color: 'red' }
                ].map((option) => (
                  <label key={option.value} className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="disclosure_pace"
                      value={option.value}
                      checked={preferences.disclosure_pace === option.value}
                      onChange={(e) => setPreferences(prev => ({ ...prev, disclosure_pace: e.target.value }))}
                      className="mt-1 w-4 h-4 text-azure-600 border-stone-300 focus:ring-azure-500"
                    />
                    <div className="flex items-center space-x-2">
                      <option.icon className={`w-5 h-5 text-${option.color}-600`} />
                      <div>
                        <div className="font-medium text-slate-900">{option.label}</div>
                        <div className="text-sm text-slate-600">{getPaceDescription(option.value)}</div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Workload Capacity */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center space-x-3 mb-4">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
                <h2 className="text-xl font-semibold text-slate-900">Workload Capacity</h2>
              </div>
              <p className="text-slate-600 mb-4">
                How much time can you dedicate to college applications each week?
              </p>
              
              <div className="space-y-3">
                {[
                  { value: 'light', label: 'Light Load', color: 'emerald' },
                  { value: 'moderate', label: 'Moderate Load', color: 'azure' },
                  { value: 'intensive', label: 'Intensive Load', color: 'amber' }
                ].map((option) => (
                  <label key={option.value} className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="workload_capacity"
                      value={option.value}
                      checked={preferences.workload_capacity === option.value}
                      onChange={(e) => setPreferences(prev => ({ ...prev, workload_capacity: e.target.value }))}
                      className="mt-1 w-4 h-4 text-azure-600 border-stone-300 focus:ring-azure-500"
                    />
                    <div>
                      <div className="font-medium text-slate-900">{option.label}</div>
                      <div className="text-sm text-slate-600">{getCapacityDescription(option.value)}</div>
                    </div>
                  </label>
                ))}
              </div>

              {/* Available Hours */}
              <div className="mt-4 pt-4 border-t border-stone-200">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Available Hours Per Week
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="range"
                    min="2"
                    max="20"
                    step="1"
                    value={preferences.available_hours_per_week}
                    onChange={(e) => setPreferences(prev => ({ ...prev, available_hours_per_week: parseInt(e.target.value) }))}
                    className="flex-1 h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <span className="w-16 px-3 py-1 bg-stone-100 text-center rounded font-medium">
                    {preferences.available_hours_per_week}h
                  </span>
                </div>
              </div>
            </div>

            {/* Advanced Options */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center space-x-3 mb-4">
                <AlertCircle className="w-6 h-6 text-violet-600" />
                <h2 className="text-xl font-semibold text-slate-900">Advanced Options</h2>
              </div>
              
              <div className="space-y-4">
                {/* Early Decision Priority */}
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.early_decision_priority}
                    onChange={(e) => setPreferences(prev => ({ ...prev, early_decision_priority: e.target.checked }))}
                    className="mt-1 w-4 h-4 text-azure-600 border-stone-300 rounded focus:ring-azure-500"
                  />
                  <div>
                    <div className="font-medium text-slate-900">Early Decision Priority</div>
                    <div className="text-sm text-slate-600">
                      Prioritize ED/EA deadlines and automatically adjust other school timelines
                    </div>
                  </div>
                </label>

                {/* Academic Calendar Awareness */}
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.academic_calendar_awareness}
                    onChange={(e) => setPreferences(prev => ({ ...prev, academic_calendar_awareness: e.target.checked }))}
                    className="mt-1 w-4 h-4 text-azure-600 border-stone-300 rounded focus:ring-azure-500"
                  />
                  <div>
                    <div className="font-medium text-slate-900">Academic Calendar Awareness</div>
                    <div className="text-sm text-slate-600">
                      Avoid scheduling intensive tasks during finals weeks and holidays
                    </div>
                  </div>
                </label>

                {/* Reveal Advanced Tasks Button */}
                {!preferences.reveal_advanced_tasks && (
                  <div className="pt-4 border-t border-stone-200">
                    <button
                      onClick={handleRevealAdvancedTasks}
                      className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-azure-500 to-violet-600 text-white rounded-lg hover:from-azure-600 hover:to-violet-700 transition-all duration-200"
                    >
                      <TrendingUp className="w-4 h-4" />
                      <span>I'm Ahead of Schedule - Show Advanced Tasks</span>
                    </button>
                    <p className="text-xs text-stone-500 mt-2">
                      Reveals additional research, networking, and optimization tasks for high-performers
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Current Settings Summary */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Current Settings</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Pace:</span>
                  <span className="font-medium capitalize">{preferences.disclosure_pace}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Capacity:</span>
                  <span className="font-medium capitalize">{preferences.workload_capacity}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Hours/Week:</span>
                  <span className="font-medium">{preferences.available_hours_per_week}h</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">ED Priority:</span>
                  <span className="font-medium">{preferences.early_decision_priority ? '✅' : '❌'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Advanced Tasks:</span>
                  <span className="font-medium">{preferences.reveal_advanced_tasks ? '✅' : '❌'}</span>
                </div>
              </div>
            </div>

            {/* Timeline Capacity Analysis */}
            {(() => {
              const capacity = calculateTimelineCapacity();
              const isWarning = !capacity.isCapacityAdequate;
              const isHighUtilization = capacity.utilizationRate > 80;
              
              return (
                <div className={`rounded-lg border p-6 ${
                  isWarning ? 'bg-red-50 border-red-200' : 
                  isHighUtilization ? 'bg-yellow-50 border-yellow-200' : 
                  'bg-emerald-50 border-emerald-200'
                }`}>
                  <div className="flex items-center space-x-2 mb-3">
                    {isWarning ? (
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    ) : isHighUtilization ? (
                      <Clock className="w-5 h-5 text-yellow-600" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    )}
                    <h3 className={`font-semibold ${
                      isWarning ? 'text-red-900' : 
                      isHighUtilization ? 'text-yellow-900' : 
                      'text-emerald-900'
                    }`}>
                      Timeline Capacity Analysis
                    </h3>
                  </div>
                  
                  <div className={`text-sm space-y-3 ${
                    isWarning ? 'text-red-800' : 
                    isHighUtilization ? 'text-yellow-800' : 
                    'text-emerald-800'
                  }`}>
                    {/* Capacity Summary */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="font-medium">Timeline: {capacity.currentPace} pace</p>
                        <p>~{capacity.totalHoursNeeded} hours total over {capacity.weeksAvailable} weeks</p>
                      </div>
                      <div>
                        <p className="font-medium">Your Capacity: {preferences.available_hours_per_week} hrs/week</p>
                        <p>Total available: {capacity.totalHoursAvailable} hours</p>
                      </div>
                    </div>
                    
                    {/* Utilization Rate */}
                    <div>
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Timeline Intensity:</span>
                        <span className="font-bold">{capacity.utilizationRate}% utilization</span>
                      </div>
                      <div className="w-full bg-stone-200 rounded-full h-2 mt-1">
                        <div 
                          className={`h-2 rounded-full ${
                            capacity.utilizationRate > 100 ? 'bg-red-600' :
                            capacity.utilizationRate > 80 ? 'bg-yellow-600' :
                            'bg-emerald-600'
                          }`}
                          style={{ width: `${Math.min(capacity.utilizationRate, 100)}%` }}
                        />
                      </div>
                    </div>
                    
                    {/* Warning Messages */}
                    {isWarning && (
                      <div className="mt-4 p-3 bg-red-100 rounded border border-red-300">
                        <p className="font-semibold text-red-900 mb-2">
                          ⚠️ Timeline Capacity Warning
                        </p>
                        <p className="text-red-800 mb-3">
                          With {preferences.available_hours_per_week} hours/week, these tasks will be removed or rushed:
                        </p>
                        <ul className="text-red-800 space-y-1 ml-4">
                          {capacity.compromisedTasks.map((task, index) => (
                            <li key={index} className="list-disc">{task}</li>
                          ))}
                        </ul>
                        <div className="mt-3 p-2 bg-red-200 rounded">
                          <p className="font-medium text-red-900">
                            💡 To complete all recommended tasks, consider <strong>{capacity.recommendedHours}+ hours/week</strong>
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* High Utilization Warning */}
                    {isHighUtilization && !isWarning && (
                      <div className="mt-4 p-3 bg-yellow-100 rounded border border-yellow-300">
                        <p className="font-semibold text-yellow-900">
                          ⏰ High Timeline Intensity
                        </p>
                        <p className="text-yellow-800">
                          Your timeline will be quite intensive. Consider adding buffer time for unexpected challenges.
                        </p>
                      </div>
                    )}
                    
                    {/* Success Message */}
                    {!isWarning && !isHighUtilization && (
                      <div className="mt-4 p-3 bg-emerald-100 rounded border border-emerald-300">
                        <p className="font-semibold text-emerald-900">
                          ✅ Excellent Timeline Balance
                        </p>
                        <p className="text-emerald-800">
                          Your timeline is well-balanced with buffer time for quality work and unexpected challenges.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Save Button */}
        <div className="mt-8 flex justify-end">
          <button
            onClick={savePreferences}
            disabled={saving}
            className="flex items-center space-x-2 px-6 py-3 bg-azure-600 text-white rounded-lg hover:bg-azure-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            <span>{saving ? 'Saving...' : 'Save Timeline Preferences'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentControlPanel;